import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import { fetchSeo, saveSeo } from "@/store/altingross/seo";
import PageHeader from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Globe, Share2, Twitter, BarChart3, Link2,
  Code2, Loader2, Save, CheckCircle2, Users, Upload, X,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

// ─── Sekmeler ────────────────────────────────────────────────────────────────
const TABS = [
  { id: "general",   label: "Genel SEO",      icon: Globe },
  { id: "og",        label: "Open Graph",      icon: Share2 },
  { id: "twitter",   label: "Twitter / X",     icon: Twitter },
  { id: "analytics", label: "Analitik",        icon: BarChart3 },
  { id: "social",    label: "Sosyal Profiller", icon: Users },
  { id: "advanced",  label: "Gelişmiş",        icon: Code2 },
];

// ─── Karakter sayacı ─────────────────────────────────────────────────────────
function CharCount({ value, max, warn }) {
  const len = (value ?? "").length;
  const color = len > max ? "text-red-500" : len >= warn ? "text-yellow-500" : "text-gray-400";
  return (
    <span className={`text-[11px] ${color}`}>{len}/{max}</span>
  );
}

// ─── Alan bileşeni ────────────────────────────────────────────────────────────
function Field({ label, hint, error, children, right }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium text-gray-700">{label}</Label>
        <div className="flex items-center gap-2">{right}</div>
      </div>
      {hint && <p className="text-[11px] text-gray-400">{hint}</p>}
      {children}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

// ─── Boş değerler ─────────────────────────────────────────────────────────────
const EMPTY = {
  // Genel
  siteTitle: "", metaDescription: "", keywords: "",
  robots: "index, follow", canonicalUrl: "",
  // OG
  ogTitle: "", ogDescription: "", ogType: "website",
  ogUrl: "", ogImageUrl: "", ogSiteName: "",
  ogImage: null, // File nesnesi (yeni yükleme)
  // Twitter
  twitterCard: "summary_large_image", twitterSite: "",
  twitterCreator: "", twitterTitle: "", twitterDescription: "",
  // Analytics
  googleAnalyticsId: "", googleTagManagerId: "",
  googleSearchConsoleCode: "", facebookPixelId: "",
  yandexMetricaId: "", hotjarId: "", clarityId: "",
  // Sosyal
  facebookUrl: "", instagramUrl: "", twitterUrl: "",
  linkedinUrl: "", youtubeUrl: "", whatsappNumber: "",
  pinterestUrl: "", tiktokUrl: "",
  // Gelişmiş
  robotsTxt: "", customHeadScripts: "",
  customBodyStartScripts: "", customBodyEndScripts: "",
  schemaOrg: "",
  // Ayarlar
  noIndex: false, noFollow: false,
};

const SEO_IMG = (url) => url ? `http://localhost:5297/${url}` : null;

// ─── Google önizleme ──────────────────────────────────────────────────────────
function GooglePreview({ title, description, url }) {
  const displayUrl = url || "www.altingross.com";
  const displayTitle = title || "Sayfa Başlığı";
  const displayDesc = description || "Sayfa açıklaması burada görünecek.";
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
      <p className="text-[11px] text-gray-400 mb-2 font-medium uppercase tracking-wide">Google Arama Önizlemesi</p>
      <div className="flex flex-col gap-0.5">
        <span className="text-[13px] text-gray-500">{displayUrl}</span>
        <span className="text-[17px] text-blue-700 hover:underline cursor-pointer leading-snug">{displayTitle}</span>
        <span className="text-[13px] text-gray-600 leading-relaxed">{displayDesc}</span>
      </div>
    </div>
  );
}

// ─── Twitter kart önizlemesi ──────────────────────────────────────────────────
function TwitterPreview({ card, title, description, site }) {
  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden bg-white">
      <div className={cn("bg-gray-100 flex items-center justify-center text-gray-300 text-xs",
        card === "summary_large_image" ? "h-36" : "h-20")}>
        {card === "summary_large_image" ? "Büyük görsel alanı" : "Küçük görsel"}
      </div>
      <div className="p-3 flex flex-col gap-0.5 border-t border-gray-100">
        <span className="text-xs text-gray-500">{site || "altingross.com"}</span>
        <span className="text-sm font-semibold text-gray-900 leading-snug">{title || "Tweet Başlığı"}</span>
        <span className="text-xs text-gray-500 leading-relaxed">{description || "Tweet açıklaması"}</span>
      </div>
    </div>
  );
}

// ─── Ana Bileşen ───────────────────────────────────────────────────────────────
export default function SeoSettings() {
  const dispatch = useDispatch();
  const { data, loading, saving } = useSelector((s) => s.seo);
  const [activeTab, setActiveTab] = useState("general");
  const [savedTab, setSavedTab] = useState(null);

  useEffect(() => { dispatch(fetchSeo()); }, [dispatch, activeTab]);

  const formik = useFormik({
    initialValues: EMPTY,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const res = await dispatch(saveSeo(values));
      if (!res.error) {
        toast.success("SEO ayarları kaydedildi.");
        setSavedTab(activeTab);
        setTimeout(() => setSavedTab(null), 2000);
        dispatch(fetchSeo());
      }
    },
  });

  // Veriler yüklenince formu doldur — null gelen alanlar EMPTY default'una düşer
  useEffect(() => {
    if (data) {
      const merged = { ...EMPTY };
      Object.keys(EMPTY).forEach((key) => {
        const val = data[key];
        if (val !== null && val !== undefined) merged[key] = val;
      });
      merged.ogImage = null; // File nesnesi API'den gelmez
      formik.resetForm({ values: merged });
    }
  }, [data]); // eslint-disable-line

  const f = formik.values;
  const set = (name) => (e) => formik.setFieldValue(name, e.target.value);
  const setSwitch = (name) => (v) => formik.setFieldValue(name, v);

  // ─── Sekme içerikleri ───────────────────────────────────────────────────────
  const renderTab = () => {
    switch (activeTab) {

      // ── GENEL ────────────────────────────────────────────────────────────
      case "general": return (
        <div className="flex gap-6">
          <div className="flex-1 flex flex-col gap-5">
            <Field label="Site Başlığı" hint="Tarayıcı sekmesinde ve arama sonuçlarında görünür. (50–60 karakter önerilir)"
              right={<CharCount value={f.siteTitle} max={60} warn={50} />}>
              <Input name="siteTitle" value={f.siteTitle} onChange={set("siteTitle")}
                placeholder="Altingross — Kurumsal" />
            </Field>

            <Field label="Meta Açıklama" hint="Arama sonuçlarında başlığın altında görünür. (130–160 karakter önerilir)"
              right={<CharCount value={f.metaDescription} max={160} warn={130} />}>
              <Textarea name="metaDescription" rows={3} value={f.metaDescription} onChange={set("metaDescription")}
                placeholder="Kısa, çekici ve anahtar kelime içeren açıklama…" className="resize-none" />
            </Field>

            <Field label="Anahtar Kelimeler" hint="Virgülle ayırın. Modern SEO'da artık Google tarafından doğrudan kullanılmaz.">
              <Input name="keywords" value={f.keywords} onChange={set("keywords")}
                placeholder="altingross, kurumsal, toptan satış" />
            </Field>

            <Field label="Kanonik URL" hint="Sayfa için tercih edilen URL. Yinelenen içerik sorunlarını önler.">
              <Input name="canonicalUrl" value={f.canonicalUrl} onChange={set("canonicalUrl")}
                placeholder="https://www.altingross.com" />
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Robots Direktifi" hint="Arama motorlarına tarama talimatı.">
                <select name="robots" value={f.robots} onChange={set("robots")}
                  className="h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring">
                  <option value="index, follow">index, follow</option>
                  <option value="noindex, follow">noindex, follow</option>
                  <option value="index, nofollow">index, nofollow</option>
                  <option value="noindex, nofollow">noindex, nofollow</option>
                </select>
              </Field>
              <div className="flex flex-col gap-3 pt-1">
                <div className="flex items-center gap-2 mt-5">
                  <Switch id="noIndex" checked={f.noIndex} onCheckedChange={setSwitch("noIndex")} />
                  <Label htmlFor="noIndex" className="text-sm cursor-pointer">NoIndex zorla</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch id="noFollow" checked={f.noFollow} onCheckedChange={setSwitch("noFollow")} />
                  <Label htmlFor="noFollow" className="text-sm cursor-pointer">NoFollow zorla</Label>
                </div>
              </div>
            </div>
          </div>

          {/* Önizleme */}
          <div className="w-72 flex-shrink-0">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Canlı Önizleme</p>
            <GooglePreview title={f.siteTitle} description={f.metaDescription} url={f.canonicalUrl} />
            <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-3 text-xs text-gray-500 space-y-1">
              <p className="font-semibold text-gray-600">Robots: <span className="font-normal">{f.robots}</span></p>
              {f.noIndex && <Badge variant="destructive" className="text-[10px]">NoIndex Aktif</Badge>}
              {f.noFollow && <Badge variant="destructive" className="text-[10px]">NoFollow Aktif</Badge>}
            </div>
          </div>
        </div>
      );

      // ── OPEN GRAPH ────────────────────────────────────────────────────────
      case "og": return (
        <div className="flex gap-6">
          <div className="flex-1 flex flex-col gap-5">
            <Field label="OG Site Adı">
              <Input value={f.ogSiteName} onChange={set("ogSiteName")} placeholder="Altingross" />
            </Field>
            <Field label="OG Başlık" right={<CharCount value={f.ogTitle} max={95} warn={70} />}>
              <Input value={f.ogTitle} onChange={set("ogTitle")} placeholder="Paylaşıldığında görünecek başlık" />
            </Field>
            <Field label="OG Açıklama" right={<CharCount value={f.ogDescription} max={200} warn={160} />}>
              <Textarea rows={3} value={f.ogDescription} onChange={set("ogDescription")}
                placeholder="Sosyal medyada paylaşıldığında görünecek açıklama" className="resize-none" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="OG URL">
                <Input value={f.ogUrl} onChange={set("ogUrl")} placeholder="https://www.altingross.com" />
              </Field>
              <Field label="OG Tür">
                <select value={f.ogType} onChange={set("ogType")}
                  className="h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring">
                  <option value="website">website</option>
                  <option value="article">article</option>
                  <option value="product">product</option>
                  <option value="profile">profile</option>
                </select>
              </Field>
            </div>
            <Field label="OG Görsel" hint="1200×630 px önerilir.">
              {f.ogImage ? (
                <div className="space-y-2">
                  <div className="relative rounded-lg overflow-hidden border border-gray-200">
                    <img
                      src={URL.createObjectURL(f.ogImage)}
                      alt="og-preview"
                      className="w-full h-40 object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => formik.setFieldValue("ogImage", null)}
                      className="absolute top-2 right-2 flex items-center justify-center w-6 h-6 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 truncate">{f.ogImage.name}</p>
                </div>
              ) : f.ogImageUrl ? (
                <div className="space-y-2">
                  <div className="relative rounded-lg overflow-hidden border border-gray-200">
                    <img
                      src={SEO_IMG(f.ogImageUrl)}
                      alt="og-current"
                      className="w-full h-40 object-cover"
                    />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer text-xs text-blue-600 hover:text-blue-700">
                    <Upload className="w-3.5 h-3.5" />
                    Görseli değiştir
                    <input type="file" accept="image/*" className="hidden"
                      onChange={(e) => formik.setFieldValue("ogImage", e.target.files?.[0] ?? null)} />
                  </label>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 py-8 cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-colors">
                  <Upload className="w-6 h-6 text-gray-400" />
                  <span className="text-sm text-gray-500">Dosya seç veya buraya sürükle</span>
                  <span className="text-xs text-gray-400">PNG, JPG — 1200×630 px önerilir</span>
                  <input type="file" accept="image/*" className="hidden"
                    onChange={(e) => formik.setFieldValue("ogImage", e.target.files?.[0] ?? null)} />
                </label>
              )}
            </Field>
          </div>
          <div className="w-72 flex-shrink-0">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Facebook Önizleme</p>
            <div className="rounded-xl border border-gray-200 overflow-hidden bg-white shadow-sm">
              <div className="h-36 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center text-gray-400 text-xs">
                {f.ogImage
                  ? <img src={URL.createObjectURL(f.ogImage)} alt="og" className="w-full h-full object-cover" />
                  : f.ogImageUrl
                    ? <img src={SEO_IMG(f.ogImageUrl)} alt="og" className="w-full h-full object-cover" />
                    : "OG Görsel (1200×630)"}
              </div>
              <div className="p-3 border-t border-gray-100">
                <p className="text-[10px] uppercase text-gray-400">{f.ogSiteName || "altingross.com"}</p>
                <p className="text-sm font-semibold text-gray-900 leading-snug mt-0.5">{f.ogTitle || "OG Başlık"}</p>
                <p className="text-xs text-gray-500 mt-0.5">{f.ogDescription || "OG Açıklama"}</p>
              </div>
            </div>
          </div>
        </div>
      );

      // ── TWITTER ───────────────────────────────────────────────────────────
      case "twitter": return (
        <div className="flex gap-6">
          <div className="flex-1 flex flex-col gap-5">
            <Field label="Kart Türü">
              <div className="flex gap-3">
                {["summary", "summary_large_image"].map((c) => (
                  <button key={c} type="button" onClick={() => formik.setFieldValue("twitterCard", c)}
                    className={cn("flex-1 rounded-lg border px-3 py-2 text-xs text-left transition-colors",
                      f.twitterCard === c ? "border-violet-500 bg-violet-50 text-violet-700 font-medium" : "border-gray-200 text-gray-500 hover:border-gray-300")}>
                    {c === "summary" ? "Summary (Küçük Kart)" : "Summary Large Image (Büyük Kart)"}
                  </button>
                ))}
              </div>
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Twitter Site" hint="@kullanici formatında">
                <Input value={f.twitterSite} onChange={set("twitterSite")} placeholder="@altingross" />
              </Field>
              <Field label="Twitter Creator">
                <Input value={f.twitterCreator} onChange={set("twitterCreator")} placeholder="@yazar" />
              </Field>
            </div>
            <Field label="Twitter Başlık" right={<CharCount value={f.twitterTitle} max={70} warn={50} />}>
              <Input value={f.twitterTitle} onChange={set("twitterTitle")} placeholder="Tweet başlığı" />
            </Field>
            <Field label="Twitter Açıklama" right={<CharCount value={f.twitterDescription} max={200} warn={160} />}>
              <Textarea rows={3} value={f.twitterDescription} onChange={set("twitterDescription")}
                placeholder="Twitter paylaşımında görünecek açıklama" className="resize-none" />
            </Field>
          </div>
          <div className="w-72 flex-shrink-0">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Twitter Önizleme</p>
            <TwitterPreview card={f.twitterCard} title={f.twitterTitle} description={f.twitterDescription} site={f.twitterSite} />
          </div>
        </div>
      );

      // ── ANALİTİK ─────────────────────────────────────────────────────────
      case "analytics": return (
        <div className="grid grid-cols-2 gap-5">
          {[
            { key: "googleAnalyticsId",       label: "Google Analytics 4",          hint: "Ölçüm ID'si (G-XXXXXXXXXX)", placeholder: "G-XXXXXXXXXX" },
            { key: "googleTagManagerId",      label: "Google Tag Manager",           hint: "Kapsayıcı ID (GTM-XXXXXXX)", placeholder: "GTM-XXXXXXX" },
            { key: "googleSearchConsoleCode", label: "Google Search Console",        hint: "HTML etiketi doğrulama kodu", placeholder: "XXXXXX..." },
            { key: "facebookPixelId",         label: "Facebook Pixel",               hint: "Pixel ID", placeholder: "000000000000000" },
            { key: "yandexMetricaId",         label: "Yandex Metrica",               hint: "Sayaç ID", placeholder: "00000000" },
            { key: "hotjarId",                label: "Hotjar",                       hint: "Site ID", placeholder: "0000000" },
            { key: "clarityId",               label: "Microsoft Clarity",            hint: "Proje ID", placeholder: "xxxxxxxxxx" },
          ].map(({ key, label, hint, placeholder }) => (
            <Field key={key} label={label} hint={hint}>
              <Input value={f[key]} onChange={set(key)} placeholder={placeholder} className="font-mono text-sm" />
            </Field>
          ))}
        </div>
      );

      // ── SOSYAL PROFİLLER ─────────────────────────────────────────────────
      case "social": return (
        <div className="grid grid-cols-2 gap-5">
          {[
            { key: "facebookUrl",    label: "Facebook",    placeholder: "https://facebook.com/altingross" },
            { key: "instagramUrl",   label: "Instagram",   placeholder: "https://instagram.com/altingross" },
            { key: "twitterUrl",     label: "Twitter / X", placeholder: "https://x.com/altingross" },
            { key: "linkedinUrl",    label: "LinkedIn",    placeholder: "https://linkedin.com/company/altingross" },
            { key: "youtubeUrl",     label: "YouTube",     placeholder: "https://youtube.com/@altingross" },
            { key: "whatsappNumber", label: "WhatsApp",    placeholder: "+90 555 000 00 00 (uluslararası format)" },
            { key: "pinterestUrl",   label: "Pinterest",   placeholder: "https://pinterest.com/altingross" },
            { key: "tiktokUrl",      label: "TikTok",      placeholder: "https://tiktok.com/@altingross" },
          ].map(({ key, label, placeholder }) => (
            <Field key={key} label={label}>
              <div className="relative">
                <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <Input value={f[key]} onChange={set(key)} placeholder={placeholder} className="pl-8 text-sm" />
              </div>
            </Field>
          ))}
        </div>
      );

      // ── GELİŞMİŞ ────────────────────────────────────────────────────────
      case "advanced": return (
        <div className="flex flex-col gap-5">
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-700">
            Bu alandaki scriptler ve içerikler sayfa çıktısına doğrudan eklenir. Yalnızca güvenilir kaynaklardan gelen kodları ekleyin.
          </div>
          <Field label="robots.txt İçeriği" hint="Sitenizin robots.txt dosyasının içeriği.">
            <Textarea rows={6} value={f.robotsTxt} onChange={set("robotsTxt")}
              placeholder={"User-agent: *\nAllow: /\nDisallow: /admin/\nSitemap: https://www.altingross.com/sitemap.xml"}
              className="font-mono text-xs resize-y" />
          </Field>
          <Field label="Schema.org JSON-LD" hint="Yapısal veri işaretlemesi (Organization, LocalBusiness vb.)">
            <Textarea rows={8} value={f.schemaOrg} onChange={set("schemaOrg")}
              placeholder={'{\n  "@context": "https://schema.org",\n  "@type": "Organization",\n  "name": "Altingross"\n}'}
              className="font-mono text-xs resize-y" />
          </Field>
          <div className="grid grid-cols-1 gap-5">
            <Field label="&lt;head&gt; Özel Scriptler" hint="Sayfa başına eklenir. Meta etiketleri, link etiketleri, tracking kodları.">
              <Textarea rows={4} value={f.customHeadScripts} onChange={set("customHeadScripts")}
                placeholder="<!-- <meta name='...'/> veya <script>...</script> -->"
                className="font-mono text-xs resize-y" />
            </Field>
            <Field label="&lt;body&gt; Başı Scriptleri" hint="body açılış etiketinin hemen arkasına eklenir.">
              <Textarea rows={4} value={f.customBodyStartScripts} onChange={set("customBodyStartScripts")}
                placeholder="<!-- GTM noscript veya diğer body başı kodları -->"
                className="font-mono text-xs resize-y" />
            </Field>
            <Field label="&lt;body&gt; Sonu Scriptleri" hint="body kapanış etiketinden önce eklenir.">
              <Textarea rows={4} value={f.customBodyEndScripts} onChange={set("customBodyEndScripts")}
                placeholder="<!-- Sohbet widgetları, analitik kodları vb. -->"
                className="font-mono text-xs resize-y" />
            </Field>
          </div>
        </div>
      );

      default: return null;
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        testId="seo-header"
        title="SEO Ayarları"
        description="Arama motoru optimizasyonu, sosyal medya kartları ve analitik entegrasyonlarını yönetin."
      />

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Yükleniyor…
        </div>
      ) : (
        <div className="flex gap-5 items-start">
          {/* ── Sol sekme navigasyonu ─────────────────────────────────────── */}
          <div className="w-48 flex-shrink-0 flex flex-col gap-1 sticky top-4">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isSaved = savedTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium text-left transition-colors w-full",
                    activeTab === tab.id
                      ? "bg-white text-gray-900 shadow-sm border border-gray-200"
                      : "text-gray-500 hover:bg-white/60 hover:text-gray-700"
                  )}
                >
                  {isSaved
                    ? <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                    : <Icon className="w-4 h-4 flex-shrink-0" />}
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* ── Sağ içerik ────────────────────────────────────────────────── */}
          <div className="flex-1 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {/* Sekme başlığı */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h2 className="text-base font-semibold text-gray-900">
                  {TABS.find((t) => t.id === activeTab)?.label}
                </h2>
              </div>
              <Button size="sm" onClick={formik.handleSubmit} disabled={saving}>
                {saving
                  ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Kaydediliyor…</>
                  : <><Save className="w-4 h-4 mr-1" />Kaydet</>}
              </Button>
            </div>

            {/* Sekme içeriği */}
            <div className="p-6">
              {renderTab()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
